/**
 * «Utskrifter» (MKK #34, owner 2026-10-29): the Ramadan calendar and a month's prayer timetable, to print from the
 * browser or to download as an A4 PDF.
 *
 * The table is the server's (GET /admin/printables/ramadan|month, core printables.document): the mosque's own timetable
 * with the jamaat periods and the day overrides applied, its Hijri calendar, and a dash where the timetable has no time
 * — never an invented one. The PDF (…/pdf) is the same table in Latin letters; this page may add Arabic and Urdu
 * headings (core EXTRA_HEADINGS), which the browser has fonts for and the PDF does not. Printing prints the sheet only:
 * a copy of it sits directly under <body> (a portal, hidden on screen), and while body has `printing-sheet` the print
 * stylesheet shows that copy and nothing else (panel.css). Posters to print are on «Bannere» › «Skriv ut».
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { download, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { Btn, Card, Field, LoadError, Loading, Msg, Tag } from '../components/ui';
import { Printables } from '../core';

type Kind = 'ramadan' | 'month';
const MONTHS_NO = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];

/**
 * The year a staff member most likely wants: Ramadan falls in February–March in the coming years, so from April on it
 * is next year's. Only the starting value of the field.
 */
export function defaultRamadanYear(today: Date): number { return today.getFullYear() + (today.getMonth() >= 3 ? 1 : 0); }

/** The query for the table and the PDF: the same parameters, so the two can never differ. */
export function queryOf(kind: Kind, o: { year: string; month: string; from: string; to: string; lang: string; range: boolean }): string {
  const q = new URLSearchParams();
  if (kind === 'ramadan' && o.range && o.from && o.to) { q.set('from', o.from); q.set('to', o.to); }
  else q.set('year', o.year);
  if (kind === 'month') q.set('month', o.month);
  q.set('lang', Printables.LANGS.includes(o.lang) ? o.lang : 'no');
  return q.toString();
}

/** The heading of a column, with the Arabic and/or Urdu word under it when asked for (HTML only, never the PDF). */
function Heading({ col, kind, ar, ur }: { col: Body; kind: Kind; ar: boolean; ur: boolean }) {
  const key = col.key === 'iftar' && kind !== 'ramadan' ? 'maghrib' : col.key;
  const a = ar ? Printables.EXTRA_HEADINGS.ar[key] : null;
  const u = ur ? Printables.EXTRA_HEADINGS.ur[key] : null;
  return <>{col.label}{a && <span className="ps-x" lang="ar" dir="rtl">{a}</span>}{u && <span className="ps-x" lang="ur" dir="rtl">{u}</span>}</>;
}

/** The sheet: what is printed. Plain markup and the print stylesheet; no panel chrome. */
export function Sheet({ doc, ar, ur }: { doc: Body; ar: boolean; ur: boolean }) {
  const kind: Kind = doc.kind === 'ramadan' ? 'ramadan' : 'month';
  const titleKey = kind === 'ramadan' ? 'ramadanTitle' : 'monthTitle';
  return (
    <div className="print-sheet-page" lang={doc.lang === 'en' ? 'en' : 'no'}>
      <h2 className="ps-title">{doc.title}
        {ar && <span className="ps-x" lang="ar" dir="rtl">{Printables.EXTRA_HEADINGS.ar[titleKey]}</span>}
        {ur && <span className="ps-x" lang="ur" dir="rtl">{Printables.EXTRA_HEADINGS.ur[titleKey]}</span>}</h2>
      {doc.subtitle && <p className="ps-sub">{doc.subtitle}</p>}
      <table className="ps-table">
        <thead><tr>{(doc.columns || []).map((c: Body) => <th key={c.key} scope="col" className={c.align === 'center' ? 'c' : ''}><Heading col={c} kind={kind} ar={ar} ur={ur} /></th>)}</tr></thead>
        <tbody>
          {(doc.body || []).map((r: Body) => (
            <tr key={r.date} className={[r.friday ? 'fri' : '', r.oddNight ? 'odd' : ''].filter(Boolean).join(' ') || undefined}>
              {(r.cells || []).map((v: string, i: number) => <td key={i} className={doc.columns[i] && doc.columns[i].align === 'center' ? 'c' : ''}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="ps-notes">{(doc.notes || []).map((n: string, i: number) => <li key={i}>{n}</li>)}</ul>
      <p className="ps-foot">{doc.footer}</p>
    </div>
  );
}

export function Utskrifter() {
  const now = new Date();
  const [kind, setKind] = useState<Kind>('ramadan');
  const [year, setYear] = useState(String(defaultRamadanYear(now)));
  const [mYear, setMYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [range, setRange] = useState(false);
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [lang, setLang] = useState('no');
  const [ar, setAr] = useState(false); const [ur, setUr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();

  const q = queryOf(kind, { year: kind === 'ramadan' ? year : mYear, month, from, to, lang, range });
  const valid = kind === 'month' ? /^\d{4}$/.test(mYear) : range ? Boolean(from && to) : /^\d{4}$/.test(year);
  const { data, error, reload } = useLoad<Body>(valid ? `/printables/${kind}?${q}` : null, [q]);
  // Printing prints the sheet only: the class is on <body> while the print dialog is open (panel.css @media print).
  useEffect(() => {
    const after = () => document.body.classList.remove('printing-sheet');
    window.addEventListener('afterprint', after);
    return () => { window.removeEventListener('afterprint', after); after(); };
  }, []);

  function print() { document.body.classList.add('printing-sheet'); window.print(); }
  async function pdf() {
    setBusy(true); say('');
    const name = `${kind === 'ramadan' ? 'ramadan-kalender' : 'bonnetider'}-${kind === 'ramadan' ? (range ? `${from}-${to}` : year) : `${mYear}-${month.padStart(2, '0')}`}.pdf`;
    const r = await download(`/v1/admin/printables/${kind}/pdf?${q}`, name);
    setBusy(false);
    if (!r.ok && r.status !== 401) say(r.message || 'PDF-en kunne ikke lastes ned.', 'err');
  }

  return (
    <>
      <Card title="Utskrifter" className="no-print"
            hint="Ramadan-kalender og bønnetider for en måned, fra moskeens egen bønnetidstabell — til å skrive ut eller laste ned som PDF (A4)."
            more={<>Tidene er de samme som i appen og på skjermene: tabellen med jamaat-periodene og endringene for enkeltdager.
              Der tabellen mangler en tid, står det en strek — aldri en beregnet tid. Sehri slutter når Fajr begynner. Hijri-datoene følger
              moskeens kalender, med justeringen for månesikting. Arabiske og urdu-overskrifter kommer bare med på utskriften fra nettleseren,
              ikke i PDF-en. En plakat av et banner skriver du ut fra «Bannere» › «Skriv ut».</>}>
        <div className="card-body">
          <div className="row" role="radiogroup" aria-label="Hva vil du skrive ut?" style={{ marginBottom: 12 }}>
            <label className="chk"><input type="radio" name="pr-kind" checked={kind === 'ramadan'} onChange={() => setKind('ramadan')} /> Ramadan-kalender</label>
            <label className="chk"><input type="radio" name="pr-kind" checked={kind === 'month'} onChange={() => setKind('month')} /> Bønnetider for en måned</label>
          </div>
          <div className="row" style={{ alignItems: 'flex-end' }}>
            {kind === 'ramadan' && !range && <Field label="År" note="Ramadan som begynner dette året" className="field half">{(id, d) =>
              <input id={id} aria-describedby={d} type="number" min={2000} max={2200} value={year} onChange={(e) => setYear(e.target.value)} />}</Field>}
            {kind === 'ramadan' && range && <>
              <Field label="Fra" className="field half">{(id) => <input id={id} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}</Field>
              <Field label="Til" className="field half">{(id) => <input id={id} type="date" value={to} onChange={(e) => setTo(e.target.value)} />}</Field>
            </>}
            {kind === 'month' && <>
              <Field label="År" className="field half">{(id) => <input id={id} type="number" min={2000} max={2200} value={mYear} onChange={(e) => setMYear(e.target.value)} />}</Field>
              <Field label="Måned" className="field half">{(id) =>
                <select id={id} value={month} onChange={(e) => setMonth(e.target.value)}>
                  {MONTHS_NO.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                </select>}</Field>
            </>}
            <Field label="Språk" className="field half">{(id) =>
              <select id={id} value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="no">Norsk</option><option value="en">English</option>
              </select>}</Field>
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            {kind === 'ramadan' && <label className="chk"><input type="checkbox" checked={range} onChange={(e) => setRange(e.target.checked)} /> Velg dagene selv</label>}
            <label className="chk"><input type="checkbox" checked={ar} onChange={(e) => setAr(e.target.checked)} /> Overskrifter også på arabisk</label>
            <label className="chk"><input type="checkbox" checked={ur} onChange={(e) => setUr(e.target.checked)} /> Overskrifter også på urdu</label>
          </div>
          <div className="row" style={{ marginTop: 14 }}>
            <Btn disabled={!data || busy} onClick={print}>Skriv ut</Btn>
            <Btn ghost disabled={!valid || busy} onClick={() => void pdf()}>{busy ? 'Lager PDF …' : 'Last ned PDF'}</Btn>
            {data && data.missing > 0 && <Tag tone="gold">{data.missing} {data.missing === 1 ? 'dag' : 'dager'} uten tider i tabellen</Tag>}
          </div>
          {(ar || ur) && <p className="note" style={{ marginTop: 8 }}>De arabiske og urdu-overskriftene kommer bare med når du skriver ut herfra, ikke i PDF-en.</p>}
          <Msg text={msg.text} tone={msg.tone} />
        </div>
      </Card>
      {!valid ? null : error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <div className="print-sheet" role="region" aria-label="Forhåndsvisning av utskriften">
            <Sheet doc={data} ar={ar} ur={ur} />
          </div>
          {createPortal(<div className="print-portal" aria-hidden="true"><Sheet doc={data} ar={ar} ur={ur} /></div>, document.body)}
        </>
      )}
    </>
  );
}
